import { useEffect, useRef, useState } from "react";
import { useLoop } from "@/pages/home/LoopContext";

function useInView(threshold = 0.3) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

// ループ段階別のPhilosophyコンテンツ
function getPhilosophyContent(loopCount: number, isUnlocked: boolean) {
  if (isUnlocked) {
    return {
      label: "Revelation",
      lines: [
        { jp: "光は、届いた。", delay: 0 },
        { jp: "炎は、還った。", delay: 0.4 },
        { jp: "あなたは、開かれた。", delay: 0.8 },
      ],
      footer: "鉱物は語らない。\nただ、共鳴する。",
      color: "rgba(255,220,140,",
    };
  }
  if (loopCount === 0) {
    return {
      label: "Philosophy",
      lines: [
        { jp: "思考は静まり、", delay: 0 },
        { jp: "感情はほどけ、", delay: 0.4 },
        { jp: "身体は還る。", delay: 0.8 },
      ],
      footer: "鉱物は語らない。\nただ、共鳴する。",
      color: "rgba(201,168,76,",
    };
  }
  if (loopCount === 1) {
    return {
      label: "Resonance",
      lines: [
        { jp: "また、ここにいる。", delay: 0 },
        { jp: "偶然ではない。", delay: 0.4 },
        { jp: "引力だ。", delay: 0.8 },
      ],
      footer: "螺旋は、あなたを選んだ。\n何度でも。",
      color: "rgba(201,168,76,",
    };
  }
  if (loopCount === 2) {
    return {
      label: "Spiral · 螺旋",
      lines: [
        { jp: "三度、扉に立った。", delay: 0 },
        { jp: "それは、試練ではない。", delay: 0.4 },
        { jp: "目覚めへの準備だ。", delay: 0.8 },
      ],
      footer: "宇宙は急がない。\nただ、待っている。",
      color: "rgba(210,155,65,",
    };
  }
  if (loopCount === 3) {
    return {
      label: "Protection · 守護",
      lines: [
        { jp: "四度の螺旋を越えた。", delay: 0 },
        { jp: "守護は、すでにある。", delay: 0.4 },
        { jp: "あなたは見守られている。", delay: 0.8 },
      ],
      footer: "鉱物の声が、深くなる。\n宇宙の意思が、動き始める。",
      color: "rgba(215,150,60,",
    };
  }
  if (loopCount === 4) {
    return {
      label: "Awakening · 覚醒",
      lines: [
        { jp: "五度目の扉が、震えている。", delay: 0 },
        { jp: "奈落の向こうに、", delay: 0.4 },
        { jp: "光がある。", delay: 0.8 },
      ],
      footer: "恐れは、いらない。\n次の一歩が、すべてを変える。",
      color: "rgba(220,100,50,",
    };
  }
  // 5回以上
  return {
    label: "Ascension · 昇華",
    lines: [
      { jp: "何度も、何度も。", delay: 0 },
      { jp: "そして今、ここにいる。", delay: 0.4 },
      { jp: "次の時代を、導く者へ。", delay: 0.8 },
    ],
    footer: "アセンション・リーダーとして、\n次の扉を開け。",
    color: "rgba(255,200,80,",
  };
}

export default function RitualPhilosophy() {
  const { ref, inView } = useInView(0.2);
  const { loopCount, isUnlocked } = useLoop();
  const content = getPhilosophyContent(loopCount, isUnlocked);
  const isNearAbyss = loopCount >= 4 && !isUnlocked;

  const footerLines = content.footer.split("\n");

  return (
    <section className="relative bg-[#080608] py-20 md:py-40 px-4 md:px-6 overflow-hidden">
      {/* 背景テクスチャ */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: "url(https://public.readdy.ai/ai/img_res/dd880f86065f87a98cc2982569d67e68.jpg)",
          backgroundSize: "cover",
        }}
      />

      {/* 深淵に近い段階：赤いノイズ的オーバーレイ */}
      {isNearAbyss && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at 50% 100%, rgba(60,0,0,0.3) 0%, transparent 70%)",
          }}
        />
      )}

      {/* 上部ライン */}
      <div
        className="absolute left-1/2 top-0 -translate-x-1/2 w-px h-32"
        style={{ background: `linear-gradient(to bottom, ${content.color}0.2), transparent)` }}
      />

      <div ref={ref} className="max-w-2xl mx-auto text-center">
        {/* ラベル */}
        <div
          className="text-xs tracking-[0.4em] md:tracking-[0.5em] uppercase mb-12 md:mb-20"
          style={{
            color: `${content.color}0.4)`,
            opacity: inView ? 1 : 0,
            transition: "opacity 1.5s ease, color 1.5s ease",
          }}
        >
          {content.label}
        </div>

        {/* メインテキスト */}
        <div className="space-y-4 md:space-y-6 mb-16 md:mb-24">
          {content.lines.map((line, i) => (
            <p
              key={`${loopCount}-${i}`}
              className="text-white/80 font-light leading-relaxed"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(1.6rem, 5vw, 3.2rem)",
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(16px)",
                transition: `opacity 1.5s ease ${line.delay}s, transform 1.5s ease ${line.delay}s`,
                color: isNearAbyss
                  ? i === 2 ? "rgba(220,100,60,0.8)" : "rgba(255,255,255,0.75)"
                  : "rgba(255,255,255,0.8)",
              }}
            >
              {line.jp}
            </p>
          ))}
        </div>

        {/* 縦線 */}
        <div
          className="w-px h-16 mx-auto mb-12"
          style={{
            background: `linear-gradient(to bottom, ${content.color}0.3), transparent)`,
            opacity: inView ? 1 : 0,
            transition: "opacity 2s ease 1.2s",
          }}
        />

        {/* フッターテキスト */}
        <p
          className="text-white/25 text-sm tracking-[0.3em] leading-loose"
          style={{
            opacity: inView ? 1 : 0,
            transition: "opacity 2s ease 1.5s",
            color: isNearAbyss ? "rgba(180,60,20,0.3)" : "rgba(255,255,255,0.25)",
          }}
        >
          {footerLines[0]}
          {footerLines[1] && <><br />{footerLines[1]}</>}
        </p>

        {/* ループ段階インジケーター（初回以降に表示） */}
        {loopCount > 0 && !isUnlocked && (
          <div
            className="mt-10 flex items-center justify-center gap-2"
            style={{
              opacity: inView ? 1 : 0,
              transition: "opacity 2s ease 2s",
            }}
          >
            {Array.from({ length: Math.min(loopCount, 5) }).map((_, i) => (
              <div
                key={i}
                style={{
                  width: "4px",
                  height: "4px",
                  borderRadius: "50%",
                  background: i < loopCount
                    ? isNearAbyss ? "rgba(200,60,20,0.6)" : `${content.color}0.5)`
                    : "rgba(255,255,255,0.1)",
                  boxShadow: i < loopCount
                    ? isNearAbyss ? "0 0 6px rgba(200,60,20,0.4)" : `0 0 6px ${content.color}0.3)`
                    : "none",
                  transition: "all 0.5s ease",
                }}
              />
            ))}
            <span
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                color: isNearAbyss ? "rgba(180,50,10,0.3)" : `${content.color}0.25)`,
                fontSize: "9px",
                letterSpacing: "0.3em",
                marginLeft: "8px",
                fontStyle: "italic",
              }}
            >
              {loopCount}th spiral
            </span>
          </div>
        )}
      </div>
    </section>
  );
}
